import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { VariableBlobNodeView } from '../components/VariableBlobNodeView';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableBlob: {
      insertVariableBlob: (name: string) => ReturnType;
    };
  }
}

export const VariableBlobExtension = Node.create({
  name: 'variableBlob',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: element => element.getAttribute('data-blob-name'),
        renderHTML: attributes => ({ 'data-blob-name': attributes.name }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-blob-name]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { class: 'variable-blob-node' },
        HTMLAttributes
      ),
      HTMLAttributes['data-blob-name'] || '',
    ];
  },

  renderText({ node }) {
    return `[●${node.attrs.name}●]`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableBlobNodeView);
  },

  addCommands() {
    return {
      insertVariableBlob: (name: string) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: { name },
        });
      },
    };
  },
});

/**
 * Escapes HTML special characters to prevent XSS attacks.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Converts [●name●] text patterns in HTML to span elements with data-blob-name.
 * Used when loading content from DB to transform stored format into parseable HTML.
 * HTML-escapes the name to prevent XSS attacks.
 */
export function convertBlobPatternsToHtml(html: string): string {
  return html.replace(
    /\[●([^●]+)●\]/g,
    (_match, name: string) => {
      const escapedName = escapeHtml(name);
      return `<span data-blob-name="${escapedName}" class="variable-blob-node">${escapedName}</span>`;
    }
  );
}

/**
 * Converts span elements with data-blob-name back to [●name●] text patterns.
 * Uses DOM-based parsing for safe and accurate extraction.
 * Used when saving content to DB to transform HTML into stored format.
 */
export function convertHtmlToBlobPatterns(html: string): string {
  // Use DOMParser for safe HTML parsing
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all span elements with data-blob-name attribute
  const blobSpans = doc.querySelectorAll('span[data-blob-name]');

  // Replace each span with a text node containing the pattern
  blobSpans.forEach(span => {
    const name = span.getAttribute('data-blob-name') || '';
    const textNode = doc.createTextNode(`[●${name}●]`);
    span.parentNode?.replaceChild(textNode, span);
  });

  // Return the body's innerHTML (preserves the rest of the HTML structure)
  return doc.body.innerHTML;
}
